import { useState } from 'react';
import { Card } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { cn } from '../../lib/utils';
import { StorageChart } from './ChartCard';
import { toast } from 'sonner';
import { setLicensePrice } from '../../lib/api';
import { 
  CreditCard, 
  HardDrive, 
  Users, 
  Mail,
  Cloud,
  DollarSign,
  AlertCircle,
  Download,
  FileSpreadsheet
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Format storage in GB / TB / PB based on size
const formatStorage = (gb) => {
  if (gb === null || gb === undefined || isNaN(gb)) return '—';
  if (gb >= 1024 * 1024) return `${(gb / (1024 * 1024)).toFixed(2)} PB`;
  if (gb >= 1024) return `${(gb / 1024).toFixed(2)} TB`;
  return `${gb.toFixed(1)} GB`;
};

export const M365Section = ({ licenses, storage, identity, onRefresh, className }) => {
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [newPrice, setNewPrice] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const formatCurrency = (value) => {
    if (!value) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const handleEditPrice = (license) => {
    setSelectedLicense(license);
    setNewPrice(license.price_per_user?.toString() || '');
    setPriceDialogOpen(true);
  };

  const handleSavePrice = async () => {
    if (!selectedLicense || !newPrice) return;
    
    setIsSaving(true);
    try {
      await setLicensePrice({
        sku_id: selectedLicense.sku_id,
        sku_part_number: selectedLicense.sku_part_number,
        monthly_price_per_user: parseFloat(newPrice)
      });
      toast.success('Price updated successfully');
      setPriceDialogOpen(false);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Failed to update price');
    } finally {
      setIsSaving(false);
    }
  };

  // Export functions
  const handleExportGuestUsers = async () => {
    setIsExporting(true);
    try {
      const response = await fetch(`${API_BASE}/api/reports/guest-users`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `guest-users-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Guest users report downloaded');
    } catch (error) {
      toast.error('Failed to export guest users');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportLicensedUsers = async () => {
    setIsExporting(true);
    try {
      const response = await fetch(`${API_BASE}/api/reports/licensed-users`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `licensed-users-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Licensed users report downloaded');
    } catch (error) {
      toast.error('Failed to export licensed users');
    } finally {
      setIsExporting(false);
    }
  };

  // Check data availability - show tabs with error state instead of hiding
  const hasLicenseData = licenses && licenses.length > 0;
  const hasLicenseError = !licenses || licenses.length === 0;
  
  // Storage data - check if we have actual data (not just error objects)
  const hasSharepointData = storage?.sharepoint && !storage.sharepoint.error;
  const hasOnedriveData = storage?.onedrive && !storage.onedrive.error;
  const hasExchangeData = storage?.exchange && !storage.exchange.error;
  const hasStorageData = storage && storage.data_source === 'live' && (hasSharepointData || hasOnedriveData || hasExchangeData);
  const hasStorageError = !hasStorageData;
  
  const hasIdentityData = identity && !identity.error && identity.total_users > 0;
  const hasIdentityError = !hasIdentityData;

  return (
    <Card className={cn('bg-card/80 backdrop-blur-sm border-border/50', className)} data-testid="m365-section">
      <Tabs defaultValue="licenses" className="w-full">
        <div className="p-6 pb-0">
          <TabsList className="grid w-full grid-cols-3 max-w-md">
            <TabsTrigger value="licenses" className="gap-2" data-testid="licenses-tab">
              <CreditCard className="w-4 h-4" />
              Licenses
              {hasLicenseError && <span className="text-[rgb(var(--destructive))] text-xs">Err</span>}
            </TabsTrigger>
            <TabsTrigger value="storage" className="gap-2" data-testid="storage-tab">
              <HardDrive className="w-4 h-4" />
              Storage
              {hasStorageError && <span className="text-[rgb(var(--destructive))] text-xs">Err</span>}
            </TabsTrigger>
            <TabsTrigger value="identity" className="gap-2" data-testid="identity-tab">
              <Users className="w-4 h-4" />
              Identity
              {hasIdentityError && <span className="text-[rgb(var(--destructive))] text-xs">Err</span>}
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Licenses Tab - Full Width Table */}
        <TabsContent value="licenses" className="p-6 pt-4">
          {hasLicenseData ? (
            <>
              <div className="overflow-auto">
                <Table data-testid="license-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs uppercase tracking-wider">SKU</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-center">Total</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-center">Assigned</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-center">Available</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-right">Price/User</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-right">Monthly Cost</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-center w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {licenses?.map((license, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium text-sm">
                          {license.sku_name || license.sku_part_number}
                        </TableCell>
                        <TableCell className="text-center font-mono text-sm">{license.total}</TableCell>
                        <TableCell className="text-center font-mono text-sm">{license.assigned}</TableCell>
                        <TableCell className="text-center">
                          <Badge 
                            variant={license.available > 10 ? 'default' : 'destructive'}
                            className={license.available > 10 ? 'bg-[rgb(var(--success))]/10 text-[rgb(var(--success))]' : ''}
                          >
                            {license.available}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {license.price_per_user > 0 ? `$${license.price_per_user.toFixed(2)}` : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {license.monthly_cost > 0 ? formatCurrency(license.monthly_cost) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => handleEditPrice(license)}
                            title="Set price per user"
                            data-testid={`edit-price-${license.sku_id}`}
                          >
                            <DollarSign className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* License Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-border/50">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Total SKUs</p>
                  <p className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>{licenses?.length || 0}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Total Licenses</p>
                  <p className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    {licenses?.reduce((sum, lic) => sum + lic.total, 0) || 0}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Assigned</p>
                  <p className="text-2xl font-bold text-primary" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    {licenses?.reduce((sum, lic) => sum + lic.assigned, 0) || 0}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Monthly Total</p>
                  <p className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    {formatCurrency(licenses?.reduce((sum, lic) => sum + (lic.monthly_cost || 0), 0) || 0)}
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-[rgb(var(--destructive))] opacity-50" />
              <p className="font-semibold text-[rgb(var(--destructive))]">Err: Unable to load license data</p>
              <p className="text-sm mt-2">Please check your Azure credentials and permissions</p>
            </div>
          )}
        </TabsContent>

        {/* Storage Tab */}
        <TabsContent value="storage" className="p-6 pt-4">
          {hasStorageData ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* SharePoint Storage */}
              {hasSharepointData && <StorageChart data={storage?.sharepoint} title="SharePoint Storage" />}
              {storage?.sharepoint?.error && (
                <Card className="p-6 bg-muted/30 border-border/50">
                  <div className="flex items-center gap-2 mb-4">
                    <HardDrive className="w-5 h-5 text-muted-foreground" />
                    <h4 className="font-semibold" style={{ fontFamily: 'Chivo, sans-serif' }}>SharePoint Storage</h4>
                  </div>
                  <p className="text-sm text-[rgb(var(--destructive))]">Err: {storage.sharepoint.error}</p>
                </Card>
              )}

              {/* OneDrive Usage */}
              {hasOnedriveData && (
                <Card className="p-6 bg-muted/30 border-border/50" data-testid="onedrive-card">
                  <div className="flex items-center gap-2 mb-4">
                    <Cloud className="w-5 h-5 text-primary" />
                    <h4 className="font-semibold" style={{ fontFamily: 'Chivo, sans-serif' }}>OneDrive Usage</h4>
                  </div>
                  <div className="space-y-4">
                    {/* Total Used / Available */}
                    {storage?.onedrive?.total_allocated_gb > 0 && (
                      <div className="space-y-2 pb-3 border-b border-border/50">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Total Used</span>
                          <span className="font-mono font-medium" data-testid="onedrive-total-used">
                            {formatStorage(storage?.onedrive?.total_usage_gb)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Available</span>
                          <span className="font-mono font-medium" data-testid="onedrive-available">
                            {formatStorage(storage?.onedrive?.total_available_gb)}
                          </span>
                        </div>
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Quota</span>
                          <span className="font-mono">{formatStorage(storage?.onedrive?.total_allocated_gb)} ({storage?.onedrive?.usage_percent ?? 0}% used)</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                          <div
                            className="h-full bg-primary transition-all"
                            style={{ width: `${Math.min(storage?.onedrive?.usage_percent ?? 0, 100)}%` }}
                          />
                        </div>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Users</span>
                      <span className="font-mono font-medium">{storage?.onedrive?.total_users || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Avg Usage</span>
                      <span className="font-mono font-medium">{storage?.onedrive?.avg_usage_gb?.toFixed(1) || 0} GB</span>
                    </div>
                    <div className="pt-4 border-t border-border/50">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Top Consumers</p>
                      <div className="space-y-2">
                        {storage?.onedrive?.top_consumers?.slice(0, 4).map((user, index) => (
                          <div key={index} className="flex items-center justify-between text-sm">
                            <span className="truncate mr-2">{user.user}</span>
                            <span className="font-mono text-xs">{user.usage_gb} GB</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </Card>
              )}
              {storage?.onedrive?.error && (
                <Card className="p-6 bg-muted/30 border-border/50">
                  <div className="flex items-center gap-2 mb-4">
                    <Cloud className="w-5 h-5 text-muted-foreground" />
                    <h4 className="font-semibold" style={{ fontFamily: 'Chivo, sans-serif' }}>OneDrive Usage</h4>
                  </div>
                  <p className="text-sm text-[rgb(var(--destructive))]">Err: {storage.onedrive.error}</p>
                </Card>
              )}

              {/* Exchange Storage */}
              {hasExchangeData && (
                <Card className="p-6 bg-muted/30 border-border/50" data-testid="exchange-card">
                  <div className="flex items-center gap-2 mb-4">
                    <Mail className="w-5 h-5 text-primary" />
                    <h4 className="font-semibold" style={{ fontFamily: 'Chivo, sans-serif' }}>Exchange Storage</h4>
                  </div>
                  <div className="space-y-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Mailboxes</span>
                      <span className="font-mono font-medium">{storage?.exchange?.total_mailboxes || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Total Storage</span>
                      <span className="font-mono font-medium">{storage?.exchange?.total_storage_gb || 0} GB</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Avg Mailbox</span>
                      <span className="font-mono font-medium">{storage?.exchange?.avg_mailbox_size_gb?.toFixed(1) || 0} GB</span>
                    </div>
                  </div>
                </Card>
              )}
              {storage?.exchange?.error && (
                <Card className="p-6 bg-muted/30 border-border/50">
                  <div className="flex items-center gap-2 mb-4">
                    <Mail className="w-5 h-5 text-muted-foreground" />
                    <h4 className="font-semibold" style={{ fontFamily: 'Chivo, sans-serif' }}>Exchange Storage</h4>
                  </div>
                  <p className="text-sm text-[rgb(var(--destructive))]">Err: {storage.exchange.error}</p>
                </Card>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-[rgb(var(--destructive))] opacity-50" />
              <p className="font-semibold text-[rgb(var(--destructive))]">Err: Storage data not available</p>
              <p className="text-sm mt-2">Requires Reports.Read.All permission in Azure AD</p>
            </div>
          )}
        </TabsContent>

        {/* Identity Tab - Removed MFA and SSPR cards, added export buttons */}
        <TabsContent value="identity" className="p-6 pt-4">
          {hasIdentityData ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="p-4 bg-muted/30 border-border/50 text-center" data-testid="total-users-card">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Total Users</p>
                  <p className="text-3xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>{identity?.total_users || 0}</p>
                </Card>
                <Card className="p-4 bg-muted/30 border-border/50 text-center" data-testid="member-users-card">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Members</p>
                  <p className="text-3xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>{identity?.member_users || 0}</p>
                </Card>
                <Card 
                  className="p-4 bg-muted/30 border-border/50 text-center cursor-pointer hover:bg-muted/50 transition-colors group" 
                  data-testid="guest-users-card"
                  onClick={handleExportGuestUsers}
                >
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Guests</p>
                    <Download className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <p className="text-3xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>{identity?.guest_users || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1 opacity-0 group-hover:opacity-100 transition-opacity">Click to export</p>
                </Card>
                <Card 
                  className="p-4 bg-muted/30 border-border/50 text-center cursor-pointer hover:bg-muted/50 transition-colors group" 
                  data-testid="licensed-users-card"
                  onClick={handleExportLicensedUsers}
                >
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Licensed</p>
                    <Download className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <p className="text-3xl font-bold text-primary" style={{ fontFamily: 'Chivo, sans-serif' }}>{identity?.licensed_users || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1 opacity-0 group-hover:opacity-100 transition-opacity">Click to export</p>
                </Card>
              </div>

              {/* Disabled Accounts */}
              <div className="mt-6">
                <Card className="p-4 bg-muted/30 border-border/50" data-testid="disabled-accounts-card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Disabled Accounts</p>
                      <p className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>{identity?.disabled_accounts || 0}</p>
                    </div>
                    <Badge variant="outline">
                      {((identity?.disabled_accounts || 0) / (identity?.total_users || 1) * 100).toFixed(1)}% of total
                    </Badge>
                  </div>
                </Card>
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-[rgb(var(--destructive))] opacity-50" />
              <p className="font-semibold text-[rgb(var(--destructive))]">Err: Unable to load identity data</p>
              <p className="text-sm mt-2">Please check your Azure credentials and User.Read.All permission</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Price Edit Dialog */}
      <Dialog open={priceDialogOpen} onOpenChange={setPriceDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="price-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Set License Price</DialogTitle>
            <DialogDescription>
              Enter the monthly price per user for {selectedLicense?.sku_name || selectedLicense?.sku_part_number}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Label htmlFor="price-input">Monthly Price per User (USD)</Label>
            <div className="relative mt-2">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="price-input"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={newPrice}
                onChange={(e) => setNewPrice(e.target.value)}
                className="pl-9"
                data-testid="price-input"
              />
            </div>
            {selectedLicense && newPrice && (
              <p className="text-sm text-muted-foreground mt-2">
                Estimated monthly cost: {formatCurrency(parseFloat(newPrice) * selectedLicense.assigned)}
                <span className="text-xs"> ({selectedLicense.assigned} users)</span>
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPriceDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSavePrice} disabled={isSaving || !newPrice} data-testid="save-price-btn">
              {isSaving ? 'Saving...' : 'Save Price'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
